/**
 * The **consequence tables**: the Lancer **Overheating** and **Structure Damage** tables,
 * implemented as **hardcoded resolver logic** — these are universal core rules, not pack
 * content, so they live in `rules` as code, not as authored overlay atoms. Each is a
 * draw-and-record pass: roll the dice, branch on the
 * table row, and **record every draw and outcome into grounded {@link ResolvedStep}s**
 * (`stress_delta`/`structure_delta`, `status_add`, `resource_set` for a meltdown
 * countdown, `destroy`) wrapped behind a record-only `table_check` witness. Everyone
 * else folds the recorded steps rules-free via `applyResolved`; only this code runs the
 * table rules. A row that depends on a die roll (the System Trauma weapons-vs-system d6,
 * the 2-stress ENGINEERING / 2-structure HULL save) draws that die here and records it.
 *
 * **The rulebook tables (core book "Mech Combat", pp. 80–81), values pinned verbatim:**
 *
 * OVERHEATING (roll 1d6 per point of stress damage marked, *including the one just
 * taken*; take the **lowest**):
 *   - **5-6 Emergency Shunt** — IMPAIRED until end of next turn.
 *   - **2-4 Destabilized Power Plant** — EXPOSED.
 *   - **single 1 — Meltdown** — depends on remaining STRESS: 3+ → EXPOSED. 2 →
 *       ENGINEERING check: pass → EXPOSED, fail → reactor meltdown after 1d6 turns.
 *       1 → reactor meltdown at end of next turn.
 *   - **multiple 1s — Irreversible Meltdown** — reactor meltdown at end of next turn.
 *
 * STRUCTURE DAMAGE (roll 1d6 per point of structure damage marked, *including the one
 * just taken*; take the **lowest**):
 *   - **5-6 Glancing Blow** — IMPAIRED until end of next turn.
 *   - **2-4 System Trauma** — roll 1d6: 1-3 destroy all weapons on a mount of your
 *       choice; 4-6 destroy a system of your choice.
 *   - **single 1 — Direct Hit** — depends on remaining STRUCTURE: 3+ → STUNNED; 2 →
 *       HULL check (pass → STUNNED, fail → destroyed); 1 → destroyed.
 *   - **multiple 1s — Crushing Hit** — destroyed.
 *
 * The *driving* mechanic (when 0 HP / heat>cap is reached, take 1 track damage → check →
 * reset HP-to-full / clear-heat → re-take excess) is the resolver's job; this module
 * resolves a **single** check given the just-decremented track and reports it.
 */

import type {
  Combatant,
  EncounterState,
  ResolvedStep,
  StatusRemoveStep,
  TableCheckStep,
  SaveStep,
  WeaponProfile,
  FrozenMount,
  FrozenSystem,
} from "@lancer-vtt/domain";
import {
  emptyEncounter,
  hasStatus,
  hasStructureTrack,
  DOWN_AND_OUT_STATUS,
  DEAD_STATUS,
  weaponInstanceKey,
  canUseLimitedWeapon,
  canUseSceneAction,
  isItemDestroyed,
  destroyedItemResourceKey,
  TG_AI,
  TG_NO_CASCADE,
  CASCADING_STATUS,
  FLYING_STATUS,
  saveBonusFor,
  statusFacetsFromInstance,
  voluntaryMoveBar,
} from "@lancer-vtt/domain";
import type { DiceSource } from "../dice";
// The System Trauma "of your choice" prompt REUSES the Brace interception transport — the identical
// `NeedsDecisionSignal` class `resolve/damageApply.ts`'s catch sites already recognize via `instanceof`
// (never a bespoke prompt type). This creates a module cycle with `./effects` (which imports
// `heatAsEnergyDamage` back from this file) — harmless: both bindings are referenced only
// inside function bodies, never at module-eval time, so ESM's lazy binding resolves fine.
import { NeedsDecisionSignal, promptingChoices, recordedChoices } from "./effects";
import { isChoiceSource, recordedAnswer, type RecordedAnswers } from "./effects/choices";
import { gatherStructureIntercept } from "./structureIntercept";
import { rollSelfCheck, settleCheck, type CheckRoll, type SelfCheckContext } from "./selfCheck";
import { drawRoll } from "./events/rollEvents";
import { createEventLoop } from "./events/loop";
import { raiseTrackEvent, scratchTarget, type CascadeContext } from "./events/trackEvents";
import { SEED_OVERLAY } from "./effects/seed";
import { MONSTROSITY_STRUCTURE_TABLE, resolveMonstrosityStructure } from "./monstrosityStructure";
import { dealDamagePacket } from "./resolve/damagePacket";

/** The result of resolving one consequence-table check. */
export type TableResult = {
  /** The grounded + witness steps to splice into the resolution (the `table_check` first). */
  steps: ResolvedStep[];
  /** Advisory notes (a meltdown countdown started, a GM-adjudicated choice deferred). */
  warnings: string[];
  /** True iff this check destroyed the combatant (so the caller stops re-checking excess). */
  destroyed: boolean;
  /**
   * Damage the row itself dealt (the Monstrosity table's Dismemberment, 1d6 Kinetic), already
   * mitigated. {@link resolveDamageCascade} takes it after the HP reset, like a hit's excess.
   */
  excessDamage?: number;
}

/** Roll `n` d6 through the authority dice, returning the faces (one per marked track point). */
function rollD6Pool(dice: DiceSource, n: number): number[] {
  const faces: number[] = [];
  for (let i = 0; i < n; i++) faces.push(dice.roll(6));
  return faces;
}

/** A reactor-meltdown countdown is a per-combatant resource. */
export const MELTDOWN_RESOURCE = "meltdown_countdown";

// ── AI cascade check ────────────────────────────────────────────────────────────────

/** Whether a frozen tag-ref list carries the given tag id (shared shape with {@link isIndestructible}). */
function hasTagId(tags: { id: string; val?: number }[] | undefined, tagId: string): boolean {
  return (tags ?? []).some((t) => t.id === tagId);
}

/**
 * Does `base` carry ≥1 **active cascade source** — an equipped system tagged `tg_ai` and NOT
 * `tg_no_cascade`? Read live off `base.equippedSystems` at
 * check time (re-derived, never a spawn-frozen flag), so cascade risk always reflects the
 * CURRENT loadout. A system carrying BOTH tags (the shipped-content shape of a comp/con-class
 * assistant unit, per its retired entry file: "Comp/con units are not true NHPs and thus
 * cannot enter cascade") is self-exempt and contributes no risk — verified against all 4 demand
 * carriers' retired entry files (`git show 4a83ea50:packs/core-rulebook/entries/<type>/<id>/candidate.json`: (`tags/tg_ai`, `tags/
 * tg_no_cascade` — "Prohibits an AI system from being recognized as a valid target for
 * Cascading", the per-SYSTEM grain this predicate mirrors — `systems/
 * ms_comp_con_class_assistant_unit`, `talents/t_technophile:1`, none of which imply a per-mech
 * exemption instead). Weapons/mods are NOT swept — mirrors `checkLoadoutInvariants`' own
 * `tg_ai` scope (`loadoutInvariants.ts`: "Systems-only per the shipped tag data").
 */
function hasActiveCascadeSource(base: Combatant["base"]): boolean {
  return (base.equippedSystems ?? []).some((s) => hasTagId(s.tags, TG_AI) && !hasTagId(s.tags, TG_NO_CASCADE));
}

/**
 * Roll the RAW **cascade check** (Core p.71 / SRD combat.json: "any time a mech with the AI tag
 * makes a structure damage or overheating check, roll 1d20. On a 1 the NHP cascades") — ridden
 * inside {@link resolveOverheating}/{@link resolveStructureDamage} themselves so EVERY check
 * event gets its own roll: a multi-structure-marking hit rolls once per System Trauma check (one
 * call to `resolveStructureDamage` per {@link resolveDamageCascade} loop iteration), never once
 * per hit and never once per turn.
 *
 * No-op (`[]`, no witness at all) when `combatant` carries no {@link hasActiveCascadeSource} —
 * a mech with no AI system, or only a No-Cascade-exempt one, never rolls. Otherwise always emits
 * a record-only `ai_cascade_check` witness (the roll happened, win or lose), plus — on a natural
 * 1 — a grounded `status_add "Cascading"` (`expiry: { kind: "until_removed" }`); the `Cascading` seed
 * entry is MARKER-ONLY — the "can only Shut Down" enforcement is the permissions gate, not
 * anything here or on the status itself.
 */
function rollCascadeCheck(combatant: Combatant, dice: DiceSource): ResolvedStep[] {
  if (!hasActiveCascadeSource(combatant.base)) return [];
  const d20 = dice.roll(20);
  const cascaded = d20 === 1;
  const steps: ResolvedStep[] = [{ op: "ai_cascade_check", target: combatant.id, d20, cascaded }];
  if (cascaded) {
    steps.push({ op: "status_add", target: combatant.id, status: CASCADING_STATUS, expiry: { kind: "until_removed" } });
  }
  return steps;
}

/**
 * Resolve a single **Overheating** check (rulebook "OVERHEATING"). `marked` is the
 * stress damage marked *including the point just taken* — i.e. `maxStress - currentStress`
 * after the `stress_delta`. `remainingStress` is `current.stress` after the decrement.
 * Rolls `marked` d6, takes the lowest, and emits the table row's grounded consequences.
 *
 * Meltdown is modeled as a countdown resource (a number on the combatant) + an advisory; this
 * module lands only the table → countdown, faithfully recorded and undoable. The per-turn
 * countdown *tick* and the blast itself live in the turn economy (`meltdown.ts`).
 */
export type ResolveOverheatingArgs = {
  combatant: Combatant;
  marked: number;
  remainingStress: number;
  dice: DiceSource;
  /**
   * `ActivationState.turnOrdinal` at resolution time
   * — stamped onto Emergency Shunt's IMPAIRED so a same-turn application (e.g. this check firing
   * off an Overwatch reaction attack during the combatant's OWN turn) doesn't expire one turn
   * early. RAW (`lancer-data` tables.json "Emergency Shunt"): "IMPAIRED until the end of your
   * next turn" — genuinely next, so this one stamps (unlike `disengage.ts`'s current-turn case).
   */
  turnOrdinal: number;
  /**
   * The state and overlay the Meltdown row's ENGINEERING check folds the roller's save/check
   * modifiers from (Power Up, Bonded, Bolster, Impaired). Every production caller passes one;
   * absent, the check rolls flat (direct table fixtures).
   */
  selfCheck?: SelfCheckContext;
};

export function resolveOverheating(args: ResolveOverheatingArgs): TableResult {
  const { combatant, marked, remainingStress, dice, turnOrdinal, selfCheck } = args;
  const pool = (): CheckRoll<number[]> => {
    const { result, id } = drawRoll({
      loop: selfCheck?.loop,
      dice,
      roll: { kind: "roll", roller: combatant.id, purpose: { kind: "overheating" }, spec: { dice: [{ count: Math.max(1, marked), sides: 6 }], accuracy: 0 } },
      draw: (src) => rollD6Pool(src, Math.max(1, marked)),
    });
    return { roll: result, ...(id !== undefined ? { id } : {}), total: Math.min(...result) };
  };
  // A reroll the mech holds (Reactor Stabilizer); the second roll stands.
  const faces = settleCheck({ ctx: selfCheck, dice, roller: combatant, purpose: { kind: "overheating" }, first: pool(), reroll: pool, label: (cur) => `Overheating roll [${cur.roll.join(", ")}]` }).standing.roll;
  const lowest = Math.min(...faces);
  const ones = faces.filter((f) => f === 1).length;
  const steps: ResolvedStep[] = [];
  const warnings: string[] = [];

  let row: string;
  if (ones >= 2) {
    // Multiple 1s — Irreversible Meltdown: reactor meltdown at end of next turn.
    row = "Irreversible Meltdown";
    pushMeltdown({ steps, warnings, combatant, turns: 1, row });
  } else if (lowest >= 5) {
    // 5-6 Emergency Shunt: IMPAIRED until end of next turn.
    row = "Emergency Shunt";
    steps.push({ op: "status_add", target: combatant.id, status: "Impaired", expiry: { kind: "turn", edge: "end", who: "target" }, appliedTurn: turnOrdinal });
  } else if (lowest >= 2) {
    // 2-4 Destabilized Power Plant: EXPOSED, unconditionally (lancer-data tables.json).
    row = "Destabilized Power Plant";
    steps.push({ op: "status_add", target: combatant.id, status: "Exposed", expiry: { kind: "until_removed" } });
  } else {
    // single 1 — Meltdown: the outcome depends on remaining stress (lancer-data tables.json;
    // Core p.81). 3+ → EXPOSED; 2 → ENGINEERING check (pass EXPOSED, fail meltdown after 1d6
    // of your turns); 1 → reactor meltdown at the end of your next turn.
    row = "Meltdown";
    if (remainingStress >= 3) {
      steps.push({ op: "status_add", target: combatant.id, status: "Exposed", expiry: { kind: "until_removed" } });
    } else if (remainingStress === 2) {
      const save = tableCheck({ selfCheck, dice, combatant, against: "engineering", steps, warnings });
      if (save.result === "pass") {
        steps.push({ op: "status_add", target: combatant.id, status: "Exposed", expiry: { kind: "until_removed" } });
      } else {
        const turns = dice.roll(6); // GM rolls 1d6 turns until meltdown.
        pushMeltdown({ steps, warnings, combatant, turns, row });
      }
    } else {
      pushMeltdown({ steps, warnings, combatant, turns: 1, row });
    }
  }

  const witness: TableCheckStep = {
    op: "table_check",
    kind: "overheating",
    target: combatant.id,
    dice: faces,
    lowest,
    ones,
    track: remainingStress,
    row,
  };
  // This check event ALSO rides the AI-tag cascade roll (RAW p.71) — a no-op for a combatant
  // with no active cascade source (see `rollCascadeCheck`'s doc).
  const cascadeSteps = rollCascadeCheck(combatant, dice);
  return { steps: [witness, ...steps, ...cascadeSteps], warnings, destroyed: false };
}

/**
 * `tg_indestructible` (Core p.105: "This equipment cannot be marked as Destroyed") — true iff
 * `tags` carries the marker.
 * The shared exclusion check {@link isWeaponDestroyable}/{@link isSystemDestroyable}/
 * {@link mountLiveWeapons} all read, so an Indestructible item is excluded from BOTH the
 * System Trauma "of your choice" PICK and the same-mount collateral-wipe BLAST RADIUS —
 * unlike Limited's charge check ({@link isWeaponDestroyable}'s own doc), Indestructible is
 * an absolute exemption, not a pick-only gate, so it belongs in every one of the three call
 * sites, not just the pick.
 */
function isIndestructible(tags: { id: string; val?: number }[] | undefined): boolean {
  return (tags ?? []).some((t) => t.id === "tg_indestructible");
}

/**
 * Is `weapon` a valid **System Trauma** destroy target (Core p.80) — not
 * already destroyed, not `tg_indestructible`-tagged, and if `tg_limited`-tagged, not out
 * of charges? Reuses the SAME Limited consumed-count predicate the resolve-path gate does
 * ({@link canUseLimitedWeapon}) rather than inventing a second charge model.
 * **Validity gates the PICK, not the blast radius** — this predicate decides whether a MOUNT is
 * a legal choice; once chosen, every live NON-indestructible weapon on it dies regardless of
 * this same charge check (see {@link mountLiveWeapons}).
 */
function isWeaponDestroyable(current: Combatant["current"], weapon: WeaponProfile): boolean {
  const key = weaponInstanceKey(weapon);
  if (isItemDestroyed(current.resources, key)) return false;
  if (isIndestructible(weapon.tags)) return false;
  if (weapon.limited !== undefined && !canUseLimitedWeapon(current.affordanceState, key, weapon.limited)) return false;
  return true;
}

/** Resolve a {@link FrozenMount}'s occupied slots to their {@link WeaponProfile}s, in slot order. */
function mountWeapons(base: Combatant["base"], mount: FrozenMount): WeaponProfile[] {
  const byId = new Map((base.weapons ?? []).map((w) => [weaponInstanceKey(w), w]));
  const out: WeaponProfile[] = [];
  for (const slot of mount.slots) {
    const w = byId.get(slot.weaponInstanceId);
    if (w) out.push(w);
  }
  return out;
}

/** A mount is a valid System Trauma pick iff it bears ≥1 destroyable weapon (Core p.80). */
function mountHasDestroyableWeapon(base: Combatant["base"], current: Combatant["current"], mount: FrozenMount): boolean {
  return mountWeapons(base, mount).some((w) => isWeaponDestroyable(current, w));
}

/**
 * Every currently-**live** (non-destroyed), **non-Indestructible** weapon on `mount` — the
 * mount-destruction blast radius once a mount is CHOSEN (Core p.80: "all weapons on one mount …
 * are destroyed"). Unlike {@link isWeaponDestroyable} this does NOT filter by Limited charge
 * state — an out-of-charge Limited weapon that merely shares the chosen mount still dies as
 * collateral — invalidity gates the choice, not the blast radius —
 * but `tg_indestructible` is an absolute exemption (RAW: "cannot be marked as Destroyed"), so it
 * excludes from the collateral blast too, unlike Limited.
 */
function mountLiveWeapons(base: Combatant["base"], current: Combatant["current"], mount: FrozenMount): WeaponProfile[] {
  return mountWeapons(base, mount).filter((w) => !isItemDestroyed(current.resources, weaponInstanceKey(w)) && !isIndestructible(w.tags));
}

/**
 * Is `system` a valid System Trauma destroy target — not already destroyed, not
 * `tg_indestructible`-tagged, and if it grants a `tg_limited`-tagged action, not out of
 * charges: any of `system.id`'s granted affordance records carrying a `limited` pool that is
 * exhausted (on its `pool ?? key` slot) makes the system itself invalid. A frequency rate is not a
 * charge and does not count. Matches structurally on `source.itemId` rather than string-prefixing a
 * hand-built key (`decisions/0176`; `affordance-table` Phase 3.1). Reuses
 * {@link canUseSceneAction} (the shared read predicate) rather than inventing a new charge
 * model — never a new destroyed-set write here, only a read.
 */
function isSystemDestroyable(base: Combatant["base"], current: Combatant["current"], system: FrozenSystem): boolean {
  if (isItemDestroyed(current.resources, system.instanceId)) return false;
  if (isIndestructible(system.tags)) return false;
  for (const a of base.affordances ?? []) {
    if (a.source.kind !== "granted" || a.source.itemId !== system.id) continue;
    if (a.limited && !canUseSceneAction(current.affordanceState, a.limited.pool ?? a.key, a.limited.max)) return false;
  }
  return true;
}

/** Every valid System Trauma mount INDEX (not just the first) — {@link mountHasDestroyableWeapon} per mount, in mount order. */
function allValidMountIndices(base: Combatant["base"], current: Combatant["current"]): number[] {
  return (base.mounts ?? []).reduce<number[]>((acc, m, i) => {
    if (mountHasDestroyableWeapon(base, current, m)) acc.push(i);
    return acc;
  }, []);
}

/** Every valid System Trauma system INDEX (not just the first) — {@link isSystemDestroyable} per system, in system order. */
function allValidSystemIndices(base: Combatant["base"], current: Combatant["current"]): number[] {
  return (base.equippedSystems ?? []).reduce<number[]>((acc, s, i) => {
    if (isSystemDestroyable(base, current, s)) acc.push(i);
    return acc;
  }, []);
}

/** A mount-choice option's stable recorded-answer id (`mount:<idx>`) — disjoint from a system's own `instanceId` (`system:<i>`). */
function mountOptionId(idx: number): string {
  return `mount:${idx}`;
}

/** Parse a {@link mountOptionId} back to its mount index, or `undefined` if `id` isn't one. */
function parseMountOptionId(id: string): number | undefined {
  const m = /^mount:(\d+)$/.exec(id);
  return m ? Number(m[1]) : undefined;
}

/**
 * The System Trauma "of your choice" prompt's stable recorded-answer key — keyed on the
 * STRUCTURE VALUE being resolved (`remainingStructure`), NOT
 * call-order: a big hit blowing through several structure points in one
 * {@link resolveDamageCascade} raises several System Trauma rows, each at a DIFFERENT
 * remaining-structure value, so this key is unique per row and stable across a re-run (the
 * iterate-pure re-run-from-top replays the SAME sequence of remaining-structure values every
 * time, since prior rows' recorded choices reconstruct the shrinking valid-target set
 * identically). Exported so a test/CLI can construct the expected key without duplicating the
 * scheme.
 */
export function systemTraumaPromptId(combatantId: string, remainingStructure: number): string {
  return `system_trauma:${combatantId}:${remainingStructure}`;
}

/**
 * Is `key` a recorded/broadcast answer to a **System Trauma** "of your choice" prompt
 * ({@link systemTraumaPromptId}, `system_trauma:<combatantId>:<remainingStructure>`), de-namespaced
 * of any barrage `sub:<i>:` prefix the same defensive way
 * {@link "./reactionRegistry".isReactionPromptId} does (System Trauma prompts carry `who`, not a
 * barrage sub-index, so a `sub:` prefix isn't expected in practice — mirrored here only for
 * parity/future-proofing)? **Deliberately kept SEPARATE from `isReactionPromptId`** — System
 * Trauma is not a reaction (no defender/attacker routing/attribution), so this predicate must
 * NEVER be folded into the reaction-routing `defenders` filter (`encounterRoom.ts`). Its one job
 * is widening the cross-actor **auto-dismiss** site so a stale System Trauma modal on another
 * client (e.g. a GM's view after a player answers) clears the same way a Brace prompt's does.
 */
export function isSystemTraumaPromptId(key: string): boolean {
  const bare = key.replace(/^sub:\d+:/, "");
  return bare.startsWith("system_trauma:");
}

/**
 * The **deterministic System Trauma pick** — FIRST valid by index — read off the prompt's own
 * option list, whose head IS that pick: both branches of {@link resolveStructureDamage} build
 * `options` by mapping `allValidMountIndices`/`allValidSystemIndices` in index order, and their
 * non-interactive legs resolve through THIS function rather than re-deriving the index.
 *
 * Exported because the AI tier answers a System Trauma prompt with it (`packages/ai`'s
 * `decidePromptAnswer`). That is the whole point of the export: the bot's answer and the engine's
 * own replay / CLI / non-interactive fallback — the permanent fallback leg this file's
 * {@link resolveStructureDamage} doc names, not a scope cut — are then the same rule reached
 * through the same code, rather than two implementations that happen to agree today.
 *
 * `undefined` only for an empty option list, which no caller here constructs: a category with no
 * valid choice flips to the other, and no valid choice at all escalates to a Direct Hit before any
 * option list is built. The AI tier still halts rather than guessing on it.
 */
export function firstValidSystemTraumaOption(options: readonly { id: string }[]): string | undefined {
  return options[0]?.id;
}

/**
 * Resolve a **Direct Hit** row (Core p.80) given the remaining structure — shared by the
 * ordinary "single 1" branch below AND System Trauma's mandatory escalation (RAW: "if there are
 * no valid systems or weapons remaining, this result becomes a DIRECT HIT instead"), so the
 * consequence logic lives in exactly one place.
 */
function resolveDirectHit(args: {
  combatant: Combatant;
  remainingStructure: number;
  dice: DiceSource;
  /**
   * `ActivationState.turnOrdinal` at resolution time
   * — stamped onto Direct Hit's STUNNED. RAW (`lancer-data` tables.json "Direct Hit"): "your mech
   * is STUNNED until the end of your next turn" — genuinely next, and reachable mid-the-
   * defender's-own-turn (an Overwatch-triggered reaction attack resolves during the mover's own
   * turn), so this stamps (unlike `disengage.ts`'s deliberately-current-turn case).
   */
  turnOrdinal: number;
  /** See {@link ResolveStructureDamageArgs.selfCheck}. */
  selfCheck: SelfCheckContext | undefined;
}): { steps: ResolvedStep[]; warnings: string[]; destroyed: boolean } {
  const { combatant, remainingStructure, dice, turnOrdinal, selfCheck } = args;
  const steps: ResolvedStep[] = [];
  const warnings: string[] = [];
  let destroyed = false;
  if (remainingStructure >= 3) {
    steps.push({ op: "status_add", target: combatant.id, status: "Stunned", expiry: { kind: "turn", edge: "end", who: "target" }, appliedTurn: turnOrdinal });
  } else if (remainingStructure === 2) {
    // HULL check: pass → STUNNED; fail → destroyed.
    const save = tableCheck({ selfCheck, dice, combatant, against: "hull", steps, warnings });
    if (save.result === "pass") {
      steps.push({ op: "status_add", target: combatant.id, status: "Stunned", expiry: { kind: "turn", edge: "end", who: "target" }, appliedTurn: turnOrdinal });
    } else {
      steps.push({ op: "destroy", target: combatant.id, destroyed: true });
      destroyed = true;
    }
  } else {
    // 1 STRUCTURE: destroyed.
    steps.push({ op: "destroy", target: combatant.id, destroyed: true });
    destroyed = true;
  }
  return { steps, warnings, destroyed };
}

/**
 * Resolve a single **Structure Damage** check (rulebook "STRUCTURE DAMAGE"). `marked` is
 * the structure damage marked *including the point just taken*; `remainingStructure` is
 * `current.structure` after the decrement. Rolls `marked` d6, takes the lowest, emits the
 * row's consequences.
 *
 * **System Trauma (Core p.80) destroys real frozen items**: the category d6 (1-3 mount / 4-6
 * system) is mandatory/deterministic, but WHICH mount or system ("of your choice") is the owner's
 * choice — carried on the Brace interception seam (`NeedsDecisionSignal { who: target.id }`),
 * reused verbatim: an **unrecorded** choice in **`interactive`** mode throws to round-trip a
 * prompt (`options` = every currently-valid mount/system); a recorded answer (or
 * non-interactive/CLI/replay with none recorded) resolves deterministically — the auto-pick
 * (FIRST valid by index) is the permanent fallback leg, not a scope cut. Both RAW escalation
 * clauses are mandatory: a category with no valid choice flips to the other category; nothing
 * valid at all escalates to a Direct Hit ({@link resolveDirectHit}). The whole destruction path is
 * gated on the presence of the frozen loadout structure (`base.mounts`/`base.equippedSystems` —
 * mechs always have at least one; an extra-Structure NPC without either gets a LOUD warning
 * instead of a silent skip — NPC gear destruction is a deferred follow-on, not modeled here).
 *
 * **Multi-row sequencing**: a single big hit can call this function several times in one
 * {@link resolveDamageCascade} (once per structure point marked), each a DIFFERENT System Trauma
 * row. Each row's prompt is keyed by {@link systemTraumaPromptId} on `remainingStructure` — never
 * call-order — so the iterate-pure re-run-from-top design threads correctly: a prior row's
 * recorded choice already destroyed its item BEFORE this row's validity is recomputed (the
 * destroyed-set read is live off `combatant.current.resources`, which the resolver's local sim
 * folds forward exactly as {@link applyResolved} would), so the shrinking valid-target set is
 * reconstructed identically on every re-run.
 */
export type ResolveStructureDamageArgs = {
  combatant: Combatant;
  marked: number;
  remainingStructure: number;
  dice: DiceSource;
  /** Recorded prompt answers (`intent.choices`, or an effect context's choice source) — a System Trauma pick records under {@link systemTraumaPromptId}. */
  choices?: RecordedAnswers;
  /** Interactive mode: an unrecorded "of your choice" pick throws {@link NeedsDecisionSignal} to round-trip a prompt. */
  interactive?: boolean;
  /**
   * `ActivationState.turnOrdinal` at resolution time
   * — stamped onto Glancing Blow's IMPAIRED and (via {@link resolveDirectHit}) Direct Hit's
   * STUNNED, both genuinely-next-turn RAW durations. See {@link ResolveOverheatingArgs.turnOrdinal}
   * for the shared rationale.
   */
  turnOrdinal: number;
  /**
   * The state and overlay the Direct Hit row's HULL check folds the roller's save/check modifiers
   * from (Power Up, Bonded, Bolster, Impaired; Stunned auto-fails it). Every production caller
   * passes one; absent, the check rolls flat (direct table fixtures).
   */
  selfCheck?: SelfCheckContext;
};

export function resolveStructureDamage(args: ResolveStructureDamageArgs): TableResult {
  const { combatant, marked, remainingStructure, dice, choices, interactive, turnOrdinal, selfCheck } = args;
  // A pack table frozen at spawn (Unique Physiology's Monstrosity table) replaces this one.
  const table = combatant.base.structureTable;
  if (table?.id === MONSTROSITY_STRUCTURE_TABLE) {
    return resolveMonstrosityStructure({ combatant, table, marked, remainingStructure, dice, turnOrdinal, ...(selfCheck !== undefined ? { selfCheck } : {}) });
  }
  const faces = rollD6Pool(dice, Math.max(1, marked));
  const lowest = Math.min(...faces);
  const ones = faces.filter((f) => f === 1).length;
  const steps: ResolvedStep[] = [];
  const warnings: string[] = [];
  let destroyed = false;

  let row: string;
  if (ones >= 2) {
    // Multiple 1s — Crushing Hit: destroyed.
    row = "Crushing Hit";
    steps.push({ op: "destroy", target: combatant.id, destroyed: true });
    destroyed = true;
  } else if (lowest >= 5) {
    // 5-6 Glancing Blow: IMPAIRED until end of next turn.
    row = "Glancing Blow";
    steps.push({ op: "status_add", target: combatant.id, status: "Impaired", expiry: { kind: "turn", edge: "end", who: "target" }, appliedTurn: turnOrdinal });
  } else if (lowest >= 2) {
    // 2-4 System Trauma: roll 1d6 → 1-3 weapons-on-a-mount destroyed, 4-6 a system destroyed.
    row = "System Trauma";
    const cat = dice.roll(6);
    const mounts = combatant.base.mounts;
    const systems = combatant.base.equippedSystems;
    const hasFrozenLoadout = mounts !== undefined || systems !== undefined;
    if (!hasFrozenLoadout) {
      // An extra-Structure NPC carries no frozen loadout
      // structure at all (mechs always have at least `base.mounts`) — there is no target set
      // to destroy from. LOUD, not silent: a GM reads this as a model gap (NPC gear
      // destruction is a deferred follow-on with no home yet), never mistaking it for RAW.
      warnings.push(
        `System Trauma: destruction SKIPPED for ${combatant.name} — no frozen loadout structure ` +
          `(base.mounts/base.equippedSystems absent). This is an NPC-gear-destruction MODEL GAP, ` +
          `not a rule (mechs always have a frozen loadout) — see decisions/0078 Decision 4.`,
      );
    } else {
      const mountIdxs = allValidMountIndices(combatant.base, combatant.current);
      const systemIdxs = allValidSystemIndices(combatant.base, combatant.current);
      const validMountIdx = mountIdxs[0] ?? -1;
      const validSystemIdx = systemIdxs[0] ?? -1;
      // Category flip (mandatory, Core p.80): the rolled category has no valid choice → the
      // OTHER category (still gated by ITS OWN validity — hence the fallback-to-"none" below).
      let target: "mount" | "system" | "none";
      if (cat <= 3) {
        target = validMountIdx >= 0 ? "mount" : validSystemIdx >= 0 ? "system" : "none";
      } else {
        target = validSystemIdx >= 0 ? "system" : validMountIdx >= 0 ? "mount" : "none";
      }
      const promptId = systemTraumaPromptId(combatant.id, remainingStructure);
      if (target === "none") {
        // Direct-Hit escalation (mandatory, Core p.80): nothing valid at all — reuse the
        // existing Direct Hit branch rather than reimplementing it. No choice to make.
        warnings.push(`System Trauma: no valid mount or system on ${combatant.name} to destroy — escalates to a Direct Hit.`);
        const escalation = resolveDirectHit({ combatant, remainingStructure, dice, turnOrdinal, selfCheck });
        steps.push(...escalation.steps);
        warnings.push(...escalation.warnings);
        destroyed = escalation.destroyed;
      } else if (target === "mount") {
        // "of your choice" among every CURRENTLY-valid mount: a recorded answer wins; else
        // interactive throws to prompt; else the deterministic first-valid-by-index fallback
        // (replay / CLI / non-interactive).
        const options = mountIdxs.map((idx) => {
          const live = mountLiveWeapons(combatant.base, combatant.current, mounts![idx]!);
          return { id: mountOptionId(idx), label: `Mount ${idx} (${live.map((w) => w.name).join(", ") || "empty"})` };
        });
        const optionIds = options.map((o) => o.id);
        const recorded = recordedAnswer(choices, promptId, optionIds);
        let chosenIdx: number;
        if (recorded !== undefined && optionIds.includes(recorded)) {
          chosenIdx = parseMountOptionId(recorded)!;
        } else if (interactive) {
          throw new NeedsDecisionSignal(promptId, `System Trauma: choose a mount to destroy on ${combatant.name}.`, {
            options,
            who: combatant.id,
          });
        } else {
          // The deterministic fallback leg (replay / CLI / non-interactive): FIRST valid by index,
          // resolved through the SAME {@link firstValidSystemTraumaOption} the AI tier answers this
          // prompt with, so the two tiers agree BY CONSTRUCTION rather than by coincidence.
          chosenIdx = parseMountOptionId(firstValidSystemTraumaOption(options)!)!;
        }
        const mount = mounts![chosenIdx]!;
        const live = mountLiveWeapons(combatant.base, combatant.current, mount);
        for (const w of live) {
          steps.push({ op: "destroy_item", target: combatant.id, key: weaponInstanceKey(w), destroyed: true });
        }
        warnings.push(
          `System Trauma: all weapons on mount ${chosenIdx} of ${combatant.name} destroyed ` +
            `(${live.map((w) => w.name).join(", ") || "none"}).`,
        );
      } else {
        // "of your choice" among every CURRENTLY-valid system — same recorded/interactive/
        // deterministic-fallback shape as the mount branch, keyed under the SAME promptId (the
        // category is already fixed by this point, so mount vs. system options never collide).
        const options = systemIdxs.map((idx) => ({ id: systems![idx]!.instanceId, label: systems![idx]!.name }));
        const optionIds = options.map((o) => o.id);
        const recorded = recordedAnswer(choices, promptId, optionIds);
        let chosenIdx: number;
        if (recorded !== undefined && optionIds.includes(recorded)) {
          chosenIdx = systemIdxs.find((idx) => systems![idx]!.instanceId === recorded)!;
        } else if (interactive) {
          throw new NeedsDecisionSignal(promptId, `System Trauma: choose a system to destroy on ${combatant.name}.`, {
            options,
            who: combatant.id,
          });
        } else {
          // Same deterministic fallback leg as the mount branch, through the same shared pick.
          const fallback = firstValidSystemTraumaOption(options)!;
          chosenIdx = systemIdxs.find((idx) => systems![idx]!.instanceId === fallback)!;
        }
        const system = systems![chosenIdx]!;
        steps.push({ op: "destroy_item", target: combatant.id, key: system.instanceId, destroyed: true });
        warnings.push(`System Trauma: system "${system.name}" on ${combatant.name} destroyed.`);
      }
    }
  } else {
    // single 1 — Direct Hit: depends on remaining structure.
    row = "Direct Hit";
    const hit = resolveDirectHit({ combatant, remainingStructure, dice, turnOrdinal, selfCheck });
    steps.push(...hit.steps);
    warnings.push(...hit.warnings);
    destroyed = hit.destroyed;
  }

  const witness: TableCheckStep = {
    op: "table_check",
    kind: "structure_damage",
    target: combatant.id,
    dice: faces,
    lowest,
    ones,
    track: remainingStructure,
    row,
  };
  // This check event ALSO rides the AI-tag cascade roll (RAW p.71) — a no-op for a combatant
  // with no active cascade source (see `rollCascadeCheck`'s doc).
  const cascadeSteps = rollCascadeCheck(combatant, dice);
  return { steps: [witness, ...steps, ...cascadeSteps], warnings, destroyed };
}

/**
 * Roll a table row's self-check (the 2-stress ENGINEERING / 2-structure HULL check) through the
 * shared {@link rollSelfCheck}, pushing its steps and warnings. The combatant rolls `d20 +
 * saveBonusFor(combatant, against)` (the named axis, `decisions/0163`) plus its folded
 * accuracy against its own `saveTarget`. RAW gives the structure/stress table checks no DC at all
 * ("Roll a HULL check"), so the save target is a deliberate house calibration, left as-is by
 * `save-bonus-vs-save-target` rather than retuned inside a defect fix.
 */
function tableCheck(args: {
  selfCheck: SelfCheckContext | undefined;
  dice: DiceSource;
  combatant: Combatant;
  against: "engineering" | "hull";
  steps: ResolvedStep[];
  warnings: string[];
}): SaveStep {
  const { selfCheck, dice, combatant, against, steps, warnings } = args;
  const roll = rollSelfCheck({ ctx: selfCheck, dice, combatant, against, bonus: saveBonusFor(combatant, against), dc: combatant.base.saveTarget ?? 10, roll: "check" });
  steps.push(...roll.steps);
  warnings.push(...roll.warnings);
  return roll.save;
}

// ── the driving cascades (when 0 HP / heat>cap is reached) ─────────────────────────

/** The flier's AGILITY save on structure damage or stress: RAW names no DC, so the hazard default. */
export const FLIGHT_FALL_SAVE_DC = 10;

/**
 * RAW p.62: "Flying characters that take structure damage or stress must succeed on an AGILITY
 * save or begin falling." Once per cascade that marked net track damage of the `lost` kind (an
 * intercepted point gives it back), a surviving Flying target rolls the save through
 * {@link rollSelfCheck}, so Ace's +1 and Stunned's auto-fail fold in. A failure grounds it at once,
 * as the can't-move arm does. A row that left it unable to move (Direct Hit's Stunned) grounds it
 * without a save. There is no altitude, so a fall is losing Flying (fall damage waits on
 * `true-3d-map`).
 *
 * The heat cascade checks stress only: its no-heat-track leg re-enters {@link resolveDamageCascade},
 * which makes its own structure save.
 */
function withFlightFallSave(args: {
  target: Combatant;
  result: TableResult;
  lost: "structure_delta" | "stress_delta";
  dice: DiceSource;
  selfCheck: SelfCheckContext | undefined;
}): TableResult {
  const { target, result, lost, dice, selfCheck } = args;
  const flying = target.statuses.find((st) => st.name === FLYING_STATUS);
  if (flying === undefined || result.destroyed) return result;
  const marked = result.steps.reduce((sum, st) => (st.op === lost && st.target === target.id ? sum + st.delta : sum), 0);
  if (marked >= 0) return result;
  if (result.steps.some((st) => st.op === "status_remove" && st.target === target.id && st.status === FLYING_STATUS)) return result;
  const ground: StatusRemoveStep = { op: "status_remove", target: target.id, status: FLYING_STATUS, ...statusFacetsFromInstance(flying) };
  const added = result.steps.flatMap((st) => (st.op === "status_add" && st.target === target.id ? [{ name: st.status, expiry: { kind: "until_removed" as const } }] : []));
  if (voluntaryMoveBar({ statuses: [...target.statuses, ...added] }) !== null) return { ...result, steps: [...result.steps, ground] };
  const roll = rollSelfCheck({ ctx: selfCheck, dice, combatant: target, against: "agility", bonus: saveBonusFor(target, "agility"), dc: FLIGHT_FALL_SAVE_DC, roll: "save" });
  return {
    ...result,
    steps: [...result.steps, ...roll.steps, ...(roll.save.result === "fail" ? [ground] : [])],
    warnings: [...result.warnings, ...roll.warnings],
  };
}

/**
 * Apply `amount` damage to `target`'s HP, **cascading into structure damage** when HP
 * reaches 0 (rulebook "DAMAGE AND STRUCTURE"): the combatant takes HP damage down to 0,
 * then takes 1 structure damage, makes a Structure Damage check, resets HP to full, and
 * takes the *excess* damage — possibly looping (a big hit can blow through several
 * structure points in one go). Stops the moment a check destroys the mech (further excess
 * is wasted on a wreck). Returns the ordered grounded + witness steps to splice in.
 *
 * A combatant with **no structure track** (`maxStructure` absent) takes plain HP damage
 * floored at 0 (a pilot/object just goes down — no structure check). The `entry*` snapshot is
 * the combatant's state at resolution entry; this
 * simulates HP/structure forward locally as it builds steps (the steps fold onto the same
 * entry state at once via `applyResolved`, so the local sim must mirror that fold order).
 *
 * **The `substituteStructurePoint` mode** ({@link
 * ResolveDamageCascadeArgs.substituteStructurePoint}) is the one entry that does not start from HP
 * damage at all: it ignores `amount` and takes exactly one structure point through the same
 * emitter. Sniper's Mark is its one caller.
 *
 * **A `pilot`-kind combatant at 0 HP** is neither destroyed outright (that's
 * object/deployable, above) nor left merely clamped: the FIRST time it reaches 0 it lands
 * `"Down and Out"` (unconscious, RAW-treated as Stunned — the status's own overlay entry spreads
 * Stunned's math by reference, `seedEntries.ts`); further damage while ALREADY Down and Out lands
 * a `destroy` step (the ordinary `"Destroyed"` marker — same as any other kind) plus a `"Dead"`
 * marker beside it (mirrors the `"Vaporized"` beside-`"Destroyed"` precedent, but as a plain
 * self-inverting `status_add`, not a flag on `DestroyStep` — nothing else needs to fold on it,
 * `"Destroyed"` alone already excludes it from activation).
 */
export type ResolveDamageCascadeArgs = {
  target: Combatant;
  amount: number;
  dice: DiceSource;
  /** Threaded to every System Trauma row this cascade raises — see {@link ResolveStructureDamageArgs}. */
  choices?: RecordedAnswers;
  /** Threaded to every System Trauma row this cascade raises — see {@link ResolveStructureDamageArgs}. */
  interactive?: boolean;
  /** Threaded to {@link resolveStructureDamage}'s Direct Hit/Glancing Blow — see {@link ResolveOverheatingArgs.turnOrdinal}. */
  turnOrdinal: number;
  /**
   * **The structure-damage SUBSTITUTION mode** (`npc-features-fielded-roster` Phase 8.1, Sniper's
   * Mark: *"the ANTI-MATERIEL RIFLE deals 1 structure damage instead of its usual damage"*).
   *
   * `true` ⇒ this call takes **exactly one structure point and nothing else**: `amount` is ignored
   * entirely (pass `0`), no `hp_delta` is emitted, and the cascade returns after that one point
   * rather than looping or resetting HP. Everything the point itself goes through is unchanged —
   * the same `structure_delta -1`, the same structure-loss interception window
   * ({@link import("./structureIntercept").gatherStructureIntercept}, so a marked target with
   * Custom Paint Job still gets its prompt), the same 1-Structure NPC streamline, the same
   * Structure Damage table — because it runs the SAME emitter the HP-driven loop calls
   * (`takeOneStructurePoint`), never a second copy of it.
   *
   * It is an argument on this function rather than a sibling exported function precisely so that
   * emitter stays single: a standalone "take one structure point" entry point would have had to
   * duplicate the interception window, the streamline branch, the table call and their three
   * cascade-local accumulators, which is the drift this repo's structure code can least afford.
   *
   * The caller owns the eligibility gates (immunity, and that the target HAS a structure track) —
   * see {@link import("./resolve/damageApply").applyHitDamage}.
   */
  substituteStructurePoint?: boolean;
  /** Threaded to every Structure Damage row's HULL check — see {@link ResolveStructureDamageArgs.selfCheck}. */
  selfCheck?: SelfCheckContext;
};

export function resolveDamageCascade(args: ResolveDamageCascadeArgs): TableResult {
  return withFlightFallSave({ target: args.target, result: damageCascade(args), lost: "structure_delta", dice: args.dice, selfCheck: args.selfCheck });
}

function damageCascade(args: ResolveDamageCascadeArgs): TableResult {
  const { target, amount, dice, choices, interactive, turnOrdinal, selfCheck } = args;
  const substitutePoint = args.substituteStructurePoint === true;
  const steps: ResolvedStep[] = [];
  const warnings: string[] = [];
  const maxHp = target.base.maxHp;

  // No structure track → plain clamped HP damage. Capability dispatch: the branch gates on
  // {@link hasStructureTrack}, not a kind/null read, so any current or future kind with
  // `maxStructure` absent lands here uniformly.
  if (!hasStructureTrack(target)) {
    const after = Math.max(0, Math.min(maxHp, target.current.hp - amount));
    const hp = hpDelta({ id: target.id, hp: target.current.hp, amount, maxHp });
    if (hp) steps.push(hp);
    // An object/deployable has no structure track to cascade through — RAW just
    // destroys it outright at 0 HP, unlike a mech (which takes the structure-cascade
    // branch below) or a pilot (also null-structure, but merely "down" at 0 HP, not
    // destroyed — kind-gated so this doesn't regress that).
    //
    // `drone` joins this branch: RAW "if a
    // Drone reaches 0 HP, it is destroyed" — the SAME outright-destroy treatment as an
    // object/deployable, deliberately NOT the pilot's Down-and-Out path just below (a
    // drone is a non-actor character with no "unconscious" state to fall into).
    //
    // **Dispatches on the frozen `destructionOutcome` field, not `kind`.** A
    // `BIOLOGICAL_STATUS`-based dispatch does NOT work here: the hand-built pilot fixtures in
    // `tables.test.ts` deliberately omit the status yet still pin Down-and-Out. A field stamped
    // once at spawn (`spawn.ts`) sidesteps that trap.
    const destroyedObject = target.base.destructionOutcome === "destroyOutright" && after <= 0;
    if (destroyedObject) steps.push({ op: "destroy", target: target.id, destroyed: true });

    // A pilot has no structure track either, but RAW
    // treats 0 HP as unconscious ("Down and Out"), not destroyed — UNLESS this pilot was
    // ALREADY Down and Out, in which case further damage kills it outright.
    let destroyedPilot = false;
    if (target.base.destructionOutcome === "downAndOut" && after <= 0) {
      if (hasStatus(target.statuses, DOWN_AND_OUT_STATUS)) {
        steps.push({ op: "destroy", target: target.id, destroyed: true });
        steps.push({ op: "status_add", target: target.id, status: DEAD_STATUS, expiry: { kind: "scene" } });
        destroyedPilot = true;
      } else {
        steps.push({ op: "status_add", target: target.id, status: DOWN_AND_OUT_STATUS, expiry: { kind: "until_removed" } });
      }
    }
    return { steps, warnings, destroyed: destroyedObject || destroyedPilot };
  }

  const maxStructure = target.base.maxStructure!; // hasStructureTrack(target) guarantees defined.
  let hp = target.current.hp;
  let structure = target.current.structure ?? maxStructure;
  let remaining = amount;
  // A big hit can raise SEVERAL System Trauma rows in this ONE cascade. `resolveStructureDamage` never mutates `target` — it only
  // emits steps — so an earlier row's `destroy_item` must be folded into a LOCAL resources
  // overlay here before the NEXT row's validity is recomputed, or a later row would still see
  // an item this very cascade already destroyed as live (mount-validity/blast-radius reads all
  // go through `combatant.current.resources`). `applyResolved` isn't reached for real until the
  // caller commits the whole batch, so this overlay is cascade-local, discarded either way.
  let destroyedThisCascade: Record<string, number> | undefined;
  // The structure-loss interception window's two cascade-local accumulators, both mirroring
  // `destroyedThisCascade` above (undefined until the first write) and both existing for the same
  // reason — nothing this cascade emits is folded into `target` until the caller commits the batch.
  //
  //   `interceptSpendThisCascade` — the charge `resource_set` an interception emitted. Merged into
  //     the row combatant so a LATER System Trauma row's `isSystemDestroyable` sees the burned
  //     charge (an out-of-charges granted action makes its own system an invalid destroy target).
  //   `interceptOfferedThisCascade` — offer ids already resolved this cascade. A hit blowing
  //     through several structure points reaches the window once per point, so without this the
  //     same one-charge offer would be raised again at every point. It gates on ANSWERED, not on
  //     SPENT: a declined offer must not be re-raised for the remaining points of the same hit.
  let interceptSpendThisCascade: Record<string, number> | undefined;
  let interceptOfferedThisCascade: Record<string, number> | undefined;
  // Damage a table row dealt (Dismemberment), taken after this point's HP reset like a hit's excess.
  let rowDamage = 0;
  /** `target` with every cascade-local resource fold applied — the read every row/window gets. */
  const overlaidTarget = (): Combatant =>
    destroyedThisCascade === undefined && interceptSpendThisCascade === undefined
      ? target
      : {
          ...target,
          current: {
            ...target.current,
            resources: { ...target.current.resources, ...destroyedThisCascade, ...interceptSpendThisCascade },
          },
        };

  /**
   * **The ONE structure-loss emitter** — mark a structure point, offer the interception window,
   * then roll the Structure Damage table. Called by the HP-driven loop below (once per structure
   * point a big hit blows through) AND by the substitution arm just below it
   * ({@link ResolveDamageCascadeArgs.substituteStructurePoint}), so the two share one body rather
   * than two copies of the most order-sensitive block in this file.
   *
   * Mutates the cascade-local simulation it closes over (`structure`, `hp`, and the three
   * cascade-local resource/offer accumulators) and pushes onto `steps`/`warnings`. Its verdict:
   *
   * - `"destroyed"` — the combatant is gone (0 structure, a catastrophic row, or the 1-Structure
   *   NPC streamline). The caller stops; further excess damage is wasted on a wreck.
   * - `"ignored"` — an interception un-took the point ("…and ignore the damage"). The caller stops.
   * - `"survived"` — the point stands and the combatant lives. The HP-driven caller resets HP and
   *   loops; the substitution arm is done.
   */
  const takeOneStructurePoint = (): "destroyed" | "ignored" | "survived" => {
    // Take 1 structure damage and make the check.
    if (structure <= 0) {
      // Already at 0 structure — destroyed (no further structure to lose).
      steps.push({ op: "destroy", target: target.id, destroyed: true });
      return "destroyed";
    }
    structure -= 1;
    steps.push({ op: "structure_delta", target: target.id, delta: -1 });

    if (maxStructure === 1) {
      // NPC "1-Structure" streamlining (RAW p.281): most NPCs
      // are destroyed outright at 0 HP, never rolling the Structure Damage table (a real
      // 1-structure NPC has no business landing a Glancing Blow / System Trauma witness).
      // `maxStructure > 1` (extra-Structure NPCs, mechs) falls through to the standard table.
      steps.push({ op: "destroy", target: target.id, destroyed: true });
      return "destroyed";
    }

    // ── THE STRUCTURE-LOSS INTERCEPTION WINDOW ────────────────────────────────────────────────
    // Consulted AFTER the structure point is marked and BEFORE the Structure Damage table is
    // rolled. That ordering is the whole point of the site: the resolution replays from the top on
    // every round-trip, so a prompt raised here is reached before System Trauma's "which system do
    // you destroy" prompt BY CONSTRUCTION — the player is never asked to pick a system to lose to a
    // hit an interception was about to shrug off. An offer that fires un-takes the structure point
    // and ends the cascade ("ignore the damage"); one that declines or fails costs only whatever
    // charge it spent. A NON-interactive caller defaults to decline, so this window is inert for
    // it — which is the CLI / AI / replay tier, not a fixed list of call sites: the count was
    // "seven of the eight" until `silent-equipment-autopick` threaded `interactive` down the
    // `request_move` post-move chain (Phase 2) and into the Jockey Menu's Damage option
    // (Phase 3), so dangerous terrain, a mine detonation, a bulldoze run-over and a jockeying
    // rider's Damage all reach this window live, and the raw `move_token` COMMAND stays inert.
    // Read the flag, never a caller census. See
    // `./structureIntercept` for the whole mechanism, including the load-bearing rule that an
    // offer's die is drawn only AFTER its answer is known.
    const intercept = gatherStructureIntercept(
      {
        target: overlaidTarget(),
        simulatedHp: hp,
        remainingStructure: structure,
        dice,
        choices,
        interactive,
      },
      interceptOfferedThisCascade,
    );
    for (const id of intercept.offered) interceptOfferedThisCascade = { ...interceptOfferedThisCascade, [id]: 1 };
    for (const s of intercept.steps) {
      if (s.op === "resource_set") interceptSpendThisCascade = { ...interceptSpendThisCascade, [s.key]: s.val };
      if (s.op === "hp_delta") hp += s.delta;
    }
    steps.push(...intercept.steps);
    warnings.push(...intercept.warnings);
    if (intercept.ignored) {
      // "…and ignore the damage": give the structure point back and stop. The compensating
      // `structure_delta` is a real step, not a rewrite of the one already pushed — the step list is
      // the ledger, and undo inverts what was emitted.
      structure += 1;
      steps.push({ op: "structure_delta", target: target.id, delta: 1 });
      return "ignored";
    }

    const marked = maxStructure - structure; // structure damage marked, including this point.
    const rowCombatant = overlaidTarget();
    const check = resolveStructureDamage({ combatant: rowCombatant, marked, remainingStructure: structure, dice, choices, interactive, turnOrdinal, selfCheck });
    for (const s of check.steps) {
      if (s.op === "destroy_item") {
        destroyedThisCascade = { ...destroyedThisCascade, [destroyedItemResourceKey(s.key)]: 1 };
      }
    }
    steps.push(...check.steps);
    warnings.push(...check.warnings);
    rowDamage += check.excessDamage ?? 0;
    if (check.destroyed || structure <= 0) {
      // 0 structure → destroyed; or a catastrophic row destroyed it. Stop (excess wasted).
      if (!check.destroyed && structure <= 0) {
        steps.push({ op: "destroy", target: target.id, destroyed: true });
      }
      return "destroyed";
    }
    return "survived";
  };

  // ── The SUBSTITUTION arm (Sniper's Mark) ──────────────────────────────────────────────────────
  // Exactly one structure point, `amount` ignored, HP untouched: no `hp_delta` before the point
  // (nothing drove the combatant to 0) and none after it (there is no excess to carry, so the
  // loop's "reset HP to full and take the rest" is not merely skipped but meaningless here). The
  // point itself goes through the identical emitter the HP-driven loop uses, so the interception
  // window, the 1-Structure streamline and the Structure Damage table are all reached unchanged.
  if (substitutePoint) {
    const verdict = takeOneStructurePoint();
    if (verdict !== "survived" || rowDamage === 0) return { steps, warnings, destroyed: verdict === "destroyed" };
    // The row dealt damage of its own: take it through the loop below, from the untouched HP.
    remaining = 0;
  }

  while (remaining > 0 || rowDamage > 0) {
    remaining += rowDamage;
    rowDamage = 0;
    if (hp - remaining > 0) {
      // The hit doesn't drop the combatant to 0 — plain HP damage, done.
      steps.push({ op: "hp_delta", target: target.id, delta: -remaining });
      hp -= remaining;
      remaining = 0;
      break;
    }
    // The hit reaches 0 HP. Drop to 0 (the portion that gets us there), then check.
    const toZero = hp; // damage needed to reach exactly 0 from the current hp.
    if (toZero > 0) {
      steps.push({ op: "hp_delta", target: target.id, delta: -toZero });
      hp = 0;
    }
    remaining -= toZero;

    const verdict = takeOneStructurePoint();
    // Destroyed → stop (excess wasted on a wreck). Ignored → an interception un-took the point and
    // the whole cascade ends. Both were `return`s inside this loop before the emitter was
    // extracted; the verdicts carry the same control flow.
    if (verdict === "destroyed") return { steps, warnings, destroyed: true };
    if (verdict === "ignored") return { steps, warnings, destroyed: false };

    // Survived: reset HP to full, then take the excess (loop).
    if (hp < maxHp) {
      steps.push({ op: "hp_delta", target: target.id, delta: maxHp - hp });
      hp = maxHp;
    }
  }
  return { steps, warnings, destroyed: false };
}

/**
 * Apply `amount` heat to `target` (a mech), **cascading into stress damage** when heat
 * exceeds the HEAT CAP (rulebook "OVERHEATING AND STRESS"): the mech gains heat up to its
 * cap, and when it would exceed the cap it takes 1 stress, makes an Overheating check,
 * clears all heat, and takes the *excess* heat — possibly looping. At 0 stress it suffers
 * a reactor meltdown (modeled as the countdown the table sets). Returns the steps to splice.
 *
 * A combatant with **no stress track** (`maxStress` absent) just clamps heat at the cap
 * with no overheating check. A combatant with **no heat track at all**
 * (`maxHeat` absent — the Biological signature) takes the
 * incoming heat as **Energy damage** instead ({@link heatAsEnergyDamage}) — RAW p.281: "Some
 * NPCs have no Heat Cap and instead take heat as energy damage." With a stress track, the heat
 * procedure runs on the track events (`events/trackEvents.ts`), in the self-check's loop when it
 * holds one.
 */
export type ResolveHeatCascadeArgs = {
  target: Combatant;
  amount: number;
  dice: DiceSource;
  /**
   * **A pure conduit for the no-heat-track leg — this cascade's OWN table never reads it.** The
   * Overheating/Stress table has no *"of your choice"* row (a sweep of all 22,843 lines of
   * `reference/lancer-core-book.txt` finds the phrase in exactly one destruction context, `:4371`,
   * and that is the Structure Damage table's System Trauma row), so heat itself owes no pick.
   * What this pair exists for is the `maxHeat === undefined` branch below, which re-enters the
   * DAMAGE cascade through {@link heatAsEnergyDamage} and can land that row. Before
   * `silent-equipment-autopick` Phase 3 step 10 these args did not exist, so every caller that had
   * already threaded interactivity — `routeFinalDamage`'s heat branch, reached from the attack
   * spine and from `refereeGmDamage`; Jockey Menu's Shred — silently LOST it at this hop.
   */
  choices?: RecordedAnswers;
  /** Conduit-only, exactly as {@link ResolveHeatCascadeArgs.choices} — the stress table owes no prompt of its own. */
  interactive?: boolean;
  /** Threaded to {@link resolveOverheating}'s Emergency Shunt — see {@link ResolveOverheatingArgs.turnOrdinal}. */
  turnOrdinal: number;
  /** Threaded to every Overheating row's ENGINEERING check — see {@link ResolveOverheatingArgs.selfCheck}. */
  selfCheck?: SelfCheckContext;
  /** Who dealt the heat, for the no-heat-track leg's energy packet — see {@link HeatAsEnergyDamageArgs.source}. */
  source?: string | null;
  /** The no-heat-track leg's — see {@link HeatAsEnergyDamageArgs.resisted}. */
  resisted?: true;
};

/** `ctx` answering from `choices`, halting for an unanswered prompt when `interactive`. */
function withChoices(ctx: SelfCheckContext, choices: RecordedAnswers | undefined, interactive: boolean | undefined): SelfCheckContext {
  const source = choices !== undefined && isChoiceSource(choices) ? choices : interactive === true ? promptingChoices(choices) : recordedChoices(choices);
  return source === undefined ? ctx : { ...ctx, choices: source };
}

export function resolveHeatCascade(args: ResolveHeatCascadeArgs): TableResult {
  return withFlightFallSave({ target: args.target, result: heatCascade(args), lost: "stress_delta", dice: args.dice, selfCheck: args.selfCheck });
}

function heatCascade(args: ResolveHeatCascadeArgs): TableResult {
  const { target, amount, dice, choices, interactive, turnOrdinal, selfCheck, source, resisted } = args;
  const steps: ResolvedStep[] = [];
  const warnings: string[] = [];
  const maxHeat = target.base.maxHeat;
  const maxStress = target.base.maxStress;

  if (maxHeat === undefined) {
    // No heat track at all (Biological signature) — heat becomes Energy damage. `choices`/
    // `interactive` ride through unchanged: the pick this may raise is the DAMAGE cascade's
    // System Trauma row, not anything the stress table owns.
    return heatAsEnergyDamage({ target, amount, dice, choices, interactive, turnOrdinal, selfCheck, ...(source !== undefined ? { source } : {}), ...(resisted ? { resisted } : {}) });
  }
  if (maxStress === undefined) {
    // Heat but no stress track → plain clamped heat gain.
    const before = target.current.heat ?? 0;
    const after = Math.min(maxHeat, before + amount);
    if (after !== before) steps.push({ op: "heat_delta", target: target.id, delta: after - before });
    return { steps, warnings, destroyed: false };
  }

  // The heat procedure on the track events (`event-engine-cascades` C1, C2): each folds onto the
  // loop's scratch as it lands, so the procedure reads the track off it rather than a local sim.
  const base = selfCheck?.state ?? targetAlone(turnOrdinal);
  const ctx: CascadeContext = {
    state: { ...base, combatants: { ...base.combatants, [target.id]: target } },
    targetId: target.id,
    overlay: selfCheck?.overlay ?? SEED_OVERLAY,
    dice,
    ...(choices !== undefined ? { choices } : {}),
    ...(interactive !== undefined ? { interactive } : {}),
    loop: selfCheck?.loop ?? createEventLoop({ dice }),
    turnOrdinal,
  };
  // Reactor Stabilizer's keep-or-reroll asks through this cascade's own answers when the caller
  // built its self-check without them (an attack's self-heat or Heat damage), so any overheat asks.
  const overheatCheck = selfCheck !== undefined && selfCheck.choices === undefined && (choices !== undefined || interactive === true) ? withChoices(selfCheck, choices, interactive) : selfCheck;
  ctx.loop.boundary(() => takeHeat({ ctx, amount, maxHeat, maxStress, steps, warnings, selfCheck: overheatCheck }));
  return { steps, warnings, destroyed: false };
}

/** Named args for {@link takeHeat}. */
type TakeHeatArgs = { ctx: CascadeContext; amount: number; maxHeat: number; maxStress: number; steps: ResolvedStep[]; warnings: string[]; selfCheck: SelfCheckContext | undefined };

/** Land `step` as part of the record, folded onto the scratch: a plain track change no event marks. */
function ground(ctx: CascadeContext, steps: ResolvedStep[], step: ResolvedStep): void {
  steps.push(step);
  ctx.loop.land(ctx.state, [step]);
}

/**
 * Take `amount` heat (Core p.81): up to the cap it is plain heat; over it, `heat_exceeded`, whose
 * core rule marks Stress and clears the heat, and then the excess is taken again. At 0 Stress the
 * excess is taken once more and nothing further is marked.
 */
function takeHeat(args: TakeHeatArgs): void {
  const { ctx, maxHeat, steps } = args;
  const id = ctx.targetId;
  let excess = args.amount;
  while (excess > 0) {
    const heat = scratchTarget(ctx).current.heat ?? 0;
    if (heat + excess <= maxHeat) {
      ground(ctx, steps, { op: "heat_delta", target: id, delta: excess });
      return;
    }
    // Fill to the cap; what is over it is re-taken after the clear.
    const toCap = maxHeat - heat;
    if (toCap > 0) ground(ctx, steps, { op: "heat_delta", target: id, delta: toCap });
    excess -= toCap;
    if ((scratchTarget(ctx).current.stress ?? args.maxStress) <= 0) return; // no Stress left to mark.
    raiseTrackEvent({ ctx, event: { kind: "heat_exceeded", combatant: id }, steps, core: () => overheat(args) });
    if ((scratchTarget(ctx).current.stress ?? args.maxStress) <= 0) {
      ground(ctx, steps, { op: "heat_delta", target: id, delta: Math.min(maxHeat, excess) });
      return;
    }
  }
}

/** `heat_exceeded`'s core rule: "It takes 1 STRESS, makes an overheating check, and then clears all heat" (Core p.81). */
function overheat(args: TakeHeatArgs): void {
  const { ctx, steps } = args;
  const id = ctx.targetId;
  const remaining = (scratchTarget(ctx).current.stress ?? args.maxStress) - 1;
  raiseTrackEvent({ ctx, event: { kind: "stress_lost", combatant: id, remaining }, grounds: [{ op: "stress_delta", target: id, delta: -1 }], steps, core: () => stressLost(args, remaining) });
  const heat = scratchTarget(ctx).current.heat ?? 0;
  if (heat > 0) raiseTrackEvent({ ctx, event: { kind: "heat_cleared", combatant: id }, grounds: [{ op: "heat_delta", target: id, delta: -heat }], steps });
}

/** `stress_lost`'s core rules: the 1-Stress streamline, else the overheating check, and the 0-Stress meltdown. */
function stressLost(args: TakeHeatArgs, remaining: number): void {
  const { ctx, steps, warnings, maxStress, selfCheck } = args;
  const target = scratchTarget(ctx);
  if (maxStress === 1) {
    // NPC "Heat Cap → 1 Stress" streamlining (RAW p.281): exceeding the cap grants EXPOSED instead
    // of an Overheating-table roll. No `table_check` witness for this branch.
    ground(ctx, steps, { op: "status_add", target: target.id, status: "Exposed", expiry: { kind: "until_removed" } });
    return;
  }
  const marked = maxStress - remaining; // stress damage marked, including this point.
  const check = resolveOverheating({ combatant: target, marked, remainingStress: remaining, dice: ctx.dice, turnOrdinal: ctx.turnOrdinal, ...(selfCheck !== undefined ? { selfCheck } : {}) });
  // Core p.81: "When a mech reaches 0 STRESS, it suffers a reactor meltdown at the end of its next
  // turn", whatever row the check landed on (BC-072). A longer countdown is cut to 1.
  if (remaining === 0) meltdownAtZeroStress({ steps: check.steps, warnings: check.warnings, target });
  steps.push(...check.steps);
  ctx.loop.land(ctx.state, check.steps);
  warnings.push(...check.warnings);
}

/**
 * Convert `amount` heat into **Energy damage** for a combatant with no heat track at all
 * (`base.maxHeat` absent — the Biological signature: "no Heat Cap" *is* the frozen absence, no
 * separate tag needed). Core p.67, HEAT: such a character "would take heat, they instead take an
 * equivalent amount of energy damage", so it is an energy damage packet like any other
 * (`resolve/damagePacket.ts`, `event-engine-damage` R9): Exposed doubles it, Armor and Resistance
 * mitigate it, damage Immunity cancels it, Overshield takes it first, and the damage cascade
 * resolves the SAME structure consequence (including the 1-Structure short-circuit above) a weapon
 * hit would. Called both from {@link resolveHeatCascade} (a carrier's own self-heat, an attack's
 * Heat damage, a Jockey's Shred) and from the `add_heat` effect atom (`effects/interpret.ts`,
 * INVADE's 2-Heat-damage landing on such a target), so the one conversion is shared rather than
 * duplicated at each heat-application point.
 *
 * The packet folds the target's modifiers off `selfCheck.state` with `target` in it (the caller's
 * view of the target, which may be further along than that state), or off `target` alone when the
 * caller gave no self-check context.
 *
 * **This is the ONLY hop by which heat can raise a System Trauma prompt**, and since
 * `silent-equipment-autopick` Phase 3 step 10 it carries `choices`/`interactive` through instead of
 * dropping them. So a caller that is interactive stays interactive across the heat→damage
 * re-entry, and the cascade below can throw a {@link NeedsDecisionSignal} the caller must be ready
 * to catch. Of this function's two call sites, `resolveHeatCascade` threads the pair and the
 * `add_heat` atom passes neither, so INVADE's 2-Heat-damage into a Biological target still takes
 * the deterministic first-valid pick (`tables.ts`'s permanent fallback leg).
 */
export type HeatAsEnergyDamageArgs = {
  target: Combatant;
  amount: number;
  dice: DiceSource;
  /** Threaded to {@link resolveDamageCascade}'s System Trauma row — see {@link ResolveDamageCascadeArgs.choices}. */
  choices?: RecordedAnswers;
  /** Threaded to {@link resolveDamageCascade}'s System Trauma row — see {@link ResolveDamageCascadeArgs.interactive}. */
  interactive?: boolean;
  /** Threaded to {@link resolveDamageCascade} — see {@link ResolveOverheatingArgs.turnOrdinal}. */
  turnOrdinal: number;
  /** Threaded to {@link resolveDamageCascade} — see {@link ResolveStructureDamageArgs.selfCheck}. Its state and overlay are what the packet folds. */
  selfCheck?: SelfCheckContext;
  /** Who dealt the heat: an attacker-relative grant folds against them. Omitted or `null`: no source character (`effects/nonAttackResistance.ts`). */
  source?: string | null;
  /**
   * The heat was already mitigated as heat, Resistance included (an attack's or the GM's Heat
   * damage, through `routeFinalDamage`), so the energy packet doesn't halve it again.
   */
  resisted?: true;
};

export function heatAsEnergyDamage(args: HeatAsEnergyDamageArgs): TableResult {
  const { target, amount, dice, choices, interactive, turnOrdinal, selfCheck, source = null, resisted } = args;
  if (amount <= 0) return { steps: [], warnings: [], destroyed: false };
  const base = selfCheck?.state ?? targetAlone(turnOrdinal);
  const landed = dealDamagePacket({
    packet: { kind: "damage_packet", source, target: target.id, instances: [{ dtype: "energy", amount, ...(resisted ? { resisted } : {}) }], fromAttack: false, ap: false },
    state: { ...base, combatants: { ...base.combatants, [target.id]: target } },
    dice,
    ...(selfCheck?.overlay !== undefined ? { overlay: selfCheck.overlay } : {}),
    ...(choices !== undefined ? { choices } : {}),
    ...(interactive !== undefined ? { interactive } : {}),
    ...(selfCheck !== undefined ? { selfCheck } : {}),
    loop: selfCheck?.loop,
  });
  return { steps: landed.steps, warnings: landed.warnings, destroyed: landed.destroyed };
}

/** The state a packet folds when its caller has none: no combatants (the packet adds its target), at `turnOrdinal`. */
function targetAlone(turnOrdinal: number): EncounterState {
  const empty = emptyEncounter("heat-as-energy", "");
  return { ...empty, activation: { ...empty.activation, turnOrdinal } };
}

/** A plain clamped HP delta (used for the no-structure path). */
type HpDeltaArgs = { id: string; hp: number; amount: number; maxHp: number };

function hpDelta(args: HpDeltaArgs): ResolvedStep | null {
  const { id, hp, amount, maxHp } = args;
  const after = Math.max(0, Math.min(maxHp, hp - amount));
  const delta = after - hp;
  return delta === 0 ? null : { op: "hp_delta", target: id, delta };
}

/** Push a meltdown countdown (a resource set to `turns`) + its advisory. */
type PushMeltdownArgs = {
  steps: ResolvedStep[];
  warnings: string[];
  /** The countdown it replaces is the step's `prev`, so undo restores a running one (BC-078). */
  combatant: Combatant;
  turns: number;
  row: string;
};

/** Set `target`'s meltdown countdown to 1 unless it already reads 1 or less, counting a row's set in `steps`. */
function meltdownAtZeroStress(args: { steps: ResolvedStep[]; warnings: string[]; target: Combatant }): void {
  const { steps, warnings, target } = args;
  const set = steps.findLast((s) => s.op === "resource_set" && s.target === target.id && s.key === MELTDOWN_RESOURCE);
  const pending = set?.op === "resource_set" ? set.val : target.current.resources?.[MELTDOWN_RESOURCE];
  if (pending !== undefined && pending <= 1) return;
  steps.push({ op: "resource_set", target: target.id, key: MELTDOWN_RESOURCE, val: 1, prev: pending ?? 0 });
  warnings.push("0 Stress: reactor meltdown countdown started (1 turn).");
}

function pushMeltdown(args: PushMeltdownArgs): void {
  const { steps, warnings, combatant, turns, row } = args;
  steps.push({ op: "resource_set", target: combatant.id, key: MELTDOWN_RESOURCE, val: turns, prev: combatant.current.resources?.[MELTDOWN_RESOURCE] ?? 0 });
  warnings.push(
    `${row}: reactor meltdown countdown started (${turns} turn${turns === 1 ? "" : "s"}). ` +
      `The countdown tick + the meltdown blast (BURST 2, 4d6 explosive) are deferred to A5's turn economy.`,
  );
}
